import { DateTime } from '@common/data-models/complex-types/date-time';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { ErrorLS } from '@common/data-models/misc/errors';
import { StorytellingNavigation } from '@common/data-models/service/storytelling-navigation';
import { NAVIGATION_STORAGE_KEY } from '@common/enums/constants';
import { FieldDefValidations } from '@common/enums/enums';
import { environment } from '@src/environments/environment';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { HighlightPipe } from '@src/suite/common/pipes/highlight';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import moment from 'moment-timezone';
import XRegExp from 'xregexp';

import { i18nKeys } from './i18n.default';

export class Util {
    public static cast<T>(v: any): T {
        return <T>v;
    }

    public static deepCopy<T>(obj: T): T {
        if (obj === null || typeof obj !== 'object') {
            // Primitive types
            return obj;
        }

        if (obj instanceof Date) {
            // Date
            return Util.cast<T>(new Date((<any>obj).getTime()));
        }

        if (obj instanceof DateTime) {
            return Util.cast<T>(obj.deepCopy());
        }

        if (obj instanceof MultiValue) {
            return Util.cast<T>(obj.deepCopy());
        }

        if (Array.isArray(obj)) {
            // Array
            const arr: Array<any> = [];
            obj.forEach(item => {
                arr.push(Util.deepCopy(item));
            });
            return Util.cast<T>(arr);
        }

        // Object
        const clone = Util.cast<T>(new (<any>obj).constructor());
        for (const prop in obj) {
            if (obj[prop]) {
                clone[prop] = Util.deepCopy(obj[prop]);
            }
        }
        return clone;
    }

    public static url(str: string): string {
        return str.replace(/\W+/g, '_').toLowerCase();
    }

    static titleReplaceRegex = XRegExp("[^\\p{L}\\p{Nd}\\s.,\\-_!@#\\$%\\^&\\(\\)\\+\\{\\}\\[\\]:;'\\?~`]+", 'gu');
    public static escapeSpecialCharsForFileName(fields: { [id: string]: any }) {
        if (fields.Title) fields.Title = fields.Title.replace(Util.titleReplaceRegex, '').replace(/\t/g, ' ').replace('\u2022', ' ').trim();
        if (fields.Slug) fields.Slug = fields.Slug.replace(Util.titleReplaceRegex, '').replace(/\t/g, ' ').replace('\u2022', ' ').trim();
    }

    public static validatePhoneNumber(phone: string) {
        const regex = /^\+?\d+$/;
        return regex.test(phone);
    }

    public static validateEmail(email: string) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(email);
    }

    public static removeGeneratedFields(data: any, fieldDefsDict: { [id: string]: any }) {
        for (const key in data) {
            if (!fieldDefsDict[key] && key.toUpperCase() !== 'TITLE' && key.toUpperCase() !== 'SLUG' && key.toUpperCase() !== 'TYPE' && key.toUpperCase() !== 'TEXT') delete data[key];
        }
        delete data.BackCumeTime;
        delete data.BackTime;
        delete data.Break;
        delete data.CBreak;
        delete data.CumeTime;
        delete data.ExternalFiles;
        delete data.FrontTime;
        delete data.ModBy;
        delete data.ModTime;
        delete data.PageNum;
        delete data.RevisionNumber;
        delete data.RowModBy;
        delete data.RowModTime;
        delete data.pubApproved;
        delete data.Creator;
        delete data.Owner;
        delete data.MOSControl;
        delete data.Approved;
        delete data.Float;
    }

    public static fillAddressFields(item: FieldDef, generatedProps: Array<FieldDef>) {
        let innerItem = {} as FieldDef;
        innerItem.ID = `${item.ID}-streetAddress`;
        innerItem.ComplexPropInnerId = 'streetAddress';
        innerItem.DefaultCaption = `${item.DefaultCaption} - Street Address`;
        innerItem.Validation = 'Address';
        generatedProps.push(innerItem);
        innerItem = {} as FieldDef;
        innerItem.Validation = 'Address';
        innerItem.ID = `${item.ID}-locationName`;
        innerItem.ComplexPropInnerId = 'locationName';
        innerItem.DefaultCaption = `${item.DefaultCaption} - Location Name`;
        generatedProps.push(innerItem);
        innerItem = {} as FieldDef;
        innerItem.Validation = 'Address';
        innerItem.ID = `${item.ID}-city`;
        innerItem.ComplexPropInnerId = 'city';
        innerItem.DefaultCaption = `${item.DefaultCaption} - City`;
        generatedProps.push(innerItem);
        innerItem = {} as FieldDef;
        innerItem.Validation = 'Address';
        innerItem.ID = `${item.ID}-state`;
        innerItem.ComplexPropInnerId = 'state';
        innerItem.DefaultCaption = `${item.DefaultCaption} - State`;
        generatedProps.push(innerItem);
        innerItem = {} as FieldDef;
        innerItem.Validation = 'Address';
        innerItem.ID = `${item.ID}-country`;
        innerItem.ComplexPropInnerId = 'country';
        innerItem.DefaultCaption = `${item.DefaultCaption} - Country`;
        generatedProps.push(innerItem);
        innerItem = {} as FieldDef;
        innerItem.Validation = 'Address';
        innerItem.ID = `${item.ID}-formattedAddress`;
        innerItem.ComplexPropInnerId = 'formattedAddress';
        innerItem.DefaultCaption = `${item.DefaultCaption} - Formatted Address`;
        generatedProps.push(innerItem);
    }

    public static getBooleanValFromString(value): boolean {
        switch (value) {
            case 'true':
            case 'yes':
            case '1':
                return true;
            case 'false':
            case 'no':
            case '0':
            case null:
                return false;
            default:
                return Boolean(value);
        }
    }

    public static timeBufferGranularity(granularity: string): number {
        switch (granularity.toUpperCase()) {
            case 'SECONDS': {
                return 1000;
            }
            case 'MINUTES': {
                return 1000 * 60;
            }
            case 'HOURS': {
                return 1000 * 60 * 60;
            }
        }
    }

    public static saveWarningError(errorString) {
        this.saveError(errorString, 'warningErrors', false);
    }

    public static saveCriticalError(errorString, runtime) {
        this.saveError(errorString, 'criticalErrors', runtime);
    }

    public static saveError(errorString, errorType, runtime) {
        const timestamp = moment(new Date()).utc().format();
        let errorsStr = localStorage.getItem(errorType);
        let errors: Array<ErrorLS> = [];
        if (errorsStr) {
            try {
                errors = <Array<ErrorLS>>JSON.parse(errorsStr);
            } catch (err) {
                console.log(err);
            }
        }

        if (errors) {
            errors.push({ timestamp, type: runtime ? 'RUNTIME' : 'REQUEST', msg: errorString });
            if (errors.length > 50) {
                errors = errors.slice(-50);
            }
            try {
                errorsStr = JSON.stringify(errors);
            } catch (err) {
                console.log(err);
            }
            localStorage.setItem(errorType, errorsStr);
        }
    }

    public static clearLocalStorage() {
        localStorage.removeItem('password');
        localStorage.removeItem('AppId');
        localStorage.removeItem('deviceType');
        localStorage.removeItem('deviceTokenId');
        localStorage.removeItem('SessionNomTokenId');
        localStorage.removeItem('navigationData');
        localStorage.removeItem(NAVIGATION_STORAGE_KEY);
        localStorage.removeItem('idToken');
        localStorage.removeItem('providerName');
        localStorage.removeItem('inviteToken');
        localStorage.removeItem('autoLoginWrapper');
    }

    public static directoryApp(apps: Array<StorytellingNavigation>): StorytellingNavigation {
        for (const nav of apps) {
            if (nav.ExternalURL && nav.ExternalURL.toUpperCase().indexOf('#ENPS') > -1 && nav.ExternalURL.toUpperCase().indexOf('CONTACTSALL') > -1) {
                return nav;
            }
        }
        return null;
    }
}

export enum KeyName {
    Escape = 'Escape',
}

export enum Key {
    Tab = 9,
    Enter = 13,
    Escape = 27,
    UpArrow = 38,
    DownArrow = 40,

    A = 65,
    B = 66,
    C = 67,
    D = 68,
    E = 69,
    F = 70,
    G = 71,
    H = 72,
    I = 73,
    J = 74,
    K = 75,
    L = 76,
    M = 77,
    N = 78,
    O = 79,
    P = 80,
    Q = 81,
    R = 82,
    S = 83,
    T = 84,
    U = 85,
    V = 86,
    W = 87,
    X = 88,
    Y = 89,
    Z = 90,
}

export function keyboardEventKeyCode($event: KeyboardEvent): number {
    return $event.which || $event.charCode || $event.keyCode;
}

export function keyboardEventKey($event: KeyboardEvent): string {
    return $event.key || String.fromCharCode(keyboardEventKeyCode($event));
}

export function isKey($event: KeyboardEvent, ...keys: Key[]): boolean {
    return keys.indexOf(keyboardEventKeyCode($event)) !== -1;
}

export function isCharacterKey($event: KeyboardEvent): boolean {
    const keyCode = keyboardEventKeyCode($event);
    return !!String.fromCharCode(keyCode) && keyCode >= 48 && keyCode <= 90;
}

export function optionContainsTerm(item: string | number, searchedItem: string): boolean {
    return (
        String(item)
            .split(' ')
            .some(chunk => chunk?.toLowerCase().startsWith(searchedItem.trim().toLowerCase())) || item?.toString().toLowerCase().includes(searchedItem.trim().toLowerCase())
    );
}

export class InvitationStatusCodes {
    public static readonly NEW: string = 'New';
    public static readonly PENDING: string = 'Pending';
    public static readonly ACTIVE: string = 'Active';
    public static readonly ACTIVE_STATUS_VALUES = [
        { label: 'Active', value: true },
        { label: 'Disabled', value: false },
    ];
}

export class UserInfoStatuses {
    public static readonly ACTIVE: string = 'Active';
    public static readonly DISABLED: string = 'Disabled';
}

export class GlobalRoles {
    public static readonly SUPERVISOR_ID: string = 'SUPERVISOR';
    public static readonly SUPERVISOR_ITEM: { ID: string; Description: string } = { ID: GlobalRoles.SUPERVISOR_ID, Description: 'Supervisor' };
    public static readonly SERVER_MANAGER_ID: string = 'SERVER_MANAGER';
    public static readonly SERVER_MANAGER_ITEM: { ID: string; Description: string } = { ID: GlobalRoles.SERVER_MANAGER_ID, Description: 'Server Manager' };
    public static readonly RESTRICTED_USER_ID: string = 'RESTRICTED USER';
    public static readonly RESTRICTED_USER_ITEM: { ID: string; Description: string } = { ID: GlobalRoles.RESTRICTED_USER_ID, Description: 'Restricted User' };
}

export class TopicLayoutOptionsConst {
    public static readonly LIST: string = 'list';
    public static readonly TIMELINE: string = 'timeline';
    public static readonly MAP: string = 'map';
}

export function getContrastYIQ(hexcolor: string) {
    if (hexcolor.length === 4) {
        hexcolor = `#${hexcolor[1]}${hexcolor[1]}${hexcolor[2]}${hexcolor[2]}${hexcolor[3]}${hexcolor[3]}`;
    }
    const r = parseInt(hexcolor.substring(1, 3), 16);
    const g = parseInt(hexcolor.substring(3, 5), 16);
    const b = parseInt(hexcolor.substring(5, 7), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? 'black' : 'white';
}

export const populateI18n = (data: Array<{ FieldName: string; FieldValue: string }>, defaultVals: { [id: string]: string }): { [id: string]: string } => {
    for (let i = 0; i < data.length; i++) {
        if (typeof defaultVals?.[data[i]?.FieldName] !== 'undefined') {
            defaultVals[data[i].FieldName] = data[i].FieldValue ? data[i].FieldValue : '';
        } /*else {
            this.DynamicLabelMap[data[i].FieldName] = data[i].FieldValue ? data[i].FieldValue : '';
        }*/
    }

    if (window && window?.['ANDROID_WRAPPER']?.setLanguageSettings) {
        try {
            window['ANDROID_WRAPPER'].setLanguageSettings(JSON.stringify(this));
        } catch (err) {
            console.log(err);
        }
    }
    return defaultVals;
};

export function getUrlPrefix(): string {
    return environment.virtual_directory ? `/${environment.virtual_directory}` : '';
}

export function getPlaceholderImages(): Array<{ thumbnail: string }> {
    const urlPrefix = getUrlPrefix();
    const placeholderImages: Array<{ thumbnail: string }> = [
        { thumbnail: `${urlPrefix}/assets/images/img-placeholder.jpg` },
        { thumbnail: `${urlPrefix}/assets/images/img-placeholder.jpg` },
        { thumbnail: `${urlPrefix}/assets/images/img-placeholder.jpg` },
        { thumbnail: `${urlPrefix}/assets/images/img-placeholder.jpg` },
        { thumbnail: `${urlPrefix}/assets/images/img-placeholder.jpg` },
        { thumbnail: `${urlPrefix}/assets/images/img-placeholder.jpg` },
    ];
    return placeholderImages;
}

export function getPlaceholderVideos(): Array<{ href: string }> {
    const urlPrefix = getUrlPrefix();
    const placeholderVideos: Array<{ href: string }> = [
        {
            href: `${urlPrefix}/assets/images/vid-placeholder.jpg`,
        },
        {
            href: `${urlPrefix}/assets/images/vid-placeholder.jpg`,
        },
        {
            href: `${urlPrefix}/assets/images/vid-placeholder.jpg`,
        },
    ];
    return placeholderVideos;
}

export const getItemString = (
    itemOrID: string | FieldDef,
    itemStringCache: Map<string, string>,
    i18n: { [id: string]: string },
    globalTablesService: GlobalTablesService,
    data: StorytellingRecord,
    getInnerProp?: (field) => string,
    highlightPipe?: HighlightPipe,
    searchString?: string,
    clientTimeZoneId?: string,
    currentTimeZoneId?: string
): string => {
    const itemID = typeof itemOrID === 'string' ? itemOrID : itemOrID?.ID;
    if (itemStringCache.has(itemID)) {
        return itemStringCache.get(itemID);
    }

    let str = '';
    const item = typeof itemOrID !== 'string' ? itemOrID : globalTablesService.FieldDefsDict[itemID];
    const itemData = data[itemID] || null;

    switch (item?.Validation?.toUpperCase()) {
        case FieldDefValidations.CHECKBOX: {
            str = itemData ? i18n[i18nKeys.PSTWebYesOption] : i18n[i18nKeys.PSTWebNoOption];
            break;
        }
        case FieldDefValidations.CHECKLISTEDIT:
        case FieldDefValidations.CHECKLIST: {
            if (itemData) {
                let options = globalTablesService.getListItemsForField(itemID);
                if (itemID === PSTFieldNames.PSTDesk) {
                    options = globalTablesService.getListItemsForDesk(data?.Type);
                }
                str = (itemData as MultiValue<string>).map(val => options.find(option => option.id === val)?.title).join(', ');
            } else {
                str = i18n[i18nKeys.PSTWebNotAssignedOption];
            }
            break;
        }
        case FieldDefValidations.STAFFIDLIST: {
            if (itemData) {
                str = (itemData as MultiValue<string>).map(val => globalTablesService.Staff[val]?.DisplayName).join(', ');
            } else {
                str = i18n[i18nKeys.PSTWebNotAssignedOption];
            }
            break;
        }
        case FieldDefValidations.COMBOBOX: {
            if (itemData) {
                const options = globalTablesService.getListItemsForField(itemID);
                str = options.find(option => option.id === itemData)?.title;
            }
            break;
        }
        case FieldDefValidations.TIME: {
            if (itemData) {
                const val = itemData;
                if (val && !val.timeZone && clientTimeZoneId && currentTimeZoneId != null && clientTimeZoneId !== currentTimeZoneId) {
                    const newDate = new Date(val?.get());
                    const convertedDate = DateTime.convertToTargetTZ(
                        new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate(), newDate.getHours(), newDate.getMinutes(), newDate.getSeconds()),
                        clientTimeZoneId,
                        currentTimeZoneId
                    );
                    const newInternalValue = new DateTime(convertedDate);
                    str = (newInternalValue as DateTime)?.toDateTimeString();
                } else {
                    str = (itemData as DateTime)?.toDateTimeString();
                }
            }
            break;
        }
        case FieldDefValidations.DATE: {
            if (itemData) {
                str = (itemData as DateTime)?.toDateString();
            }
            break;
        }
        case FieldDefValidations.ADDRESS: {
            if (itemData) {
                if (item?.ComplexPropInnerId) {
                    str = getInnerProp(item);
                } else {
                    str = itemData?.toString();
                }
            }
            break;
        }
        case FieldDefValidations.TEXT: {
            if (itemData) {
                str = (itemData as string)?.toString();
                if (str) {
                    highlightPipe.transform(str, searchString, false);
                }
            }
            break;
        }
        default: {
            str = itemData?.toString() || '';
            break;
        }
    }
    itemStringCache.set(itemID, str);
    return str;
};
